import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { SlotInfo, View } from 'react-big-calendar'
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { Card, makeStyles, useBreakpoint, useIsMobile } from '@paintscout/ui'
import {
  EventsMap,
  EventPreviewPopper,
  useClientOptions,
  useDialogs,
  useUser,
  useUsers,
  useUserPermissions
} from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import { getTimezone, groupEventsByEstimator, uuid } from '@paintscout/util/builder'
import { useCancelEventFlow } from 'src/hooks'
import { useLocation } from 'react-router-dom'
import history from 'shared/util/routing/history'
import { setUrlQuery } from 'shared/util/routing'
import { EventDialog, ViewEventsDialog } from '../../dialogs'
import { useCalendar } from './CalendarContext'
import CalendarShim from './CalendarShim'
import { DayEvent } from './day'
import { MobileMonthView, MonthColumnHeader, MonthDateHeader, MonthEvent, ShowMore } from './month'
import SalesCalendarFooter from './SalesCalendarFooter'
import { WeekColumnHeader, WeekEvent } from './week'

const NONE_IMPORTANT = 'none !important'
const ZERO_IMPORTANT = '0 !important'
// Minimum visual duration for grid events. Zero/short imports (Zapier, GCal)
// otherwise render as a clipped sliver because RBC sizes events by their span.
const MIN_EVENT_DISPLAY_MINUTES = 30
import { getEventTimes, getMapTitle, handleCalendarNavigate, toCalendarLocalDate } from './calendarUtils'
import SalesCalendarToolbar from './toolbar/SalesCalendarToolbar'
import { useNavContext } from 'src/components/common/Header/SideNav'

interface CalendarStyleArgs {
  view: View
  tab: string
  hideWeekends: boolean
  hideAllDayCell: boolean
  isXs: boolean
  sideNavWidth: number
}

const useStyles = makeStyles<Theme, CalendarStyleArgs>((theme) => ({
  root: {
    height: ({ view }) => (view === 'month' ? 'auto' : 'max(calc(100dvh - 300px), 800px)'),
    maxHeight: ({ view, tab }) => {
      if (view === 'month') return 'none'
      return view === 'day' || tab === 'map' ? '825px' : '1055px'
    },
    borderRadius: ({ tab }) => (tab === 'map' ? 0 : theme.borderRadius.md),
    display: 'flex',
    flexDirection: 'column',
    '& > .rbc-calendar': {
      flex: 1,
      minHeight: 0
    },
    // RBC default sets margin-bottom: 10px on .rbc-toolbar — drop it so the
    // toolbar sits flush against the time-view / month-view below.
    '& .rbc-toolbar': {
      marginBottom: 0
    },
    '& .rbc-month-view, & .rbc-time-view': {
      padding: ({ tab, view }) =>
        tab === 'map' || view === 'day' || view === 'week' || view === 'month' ? 0 : theme.spacing(2),
      border: 'none',
      [theme.breakpoints.down('xs')]: {
        padding: ({ tab, view }) =>
          tab === 'map' || view === 'day' || view === 'week' || view === 'month' ? 0 : theme.spacing(1)
      }
    },
    '& .rbc-month-view': {
      padding: 0,
      border: 'none',
      borderRadius: 0,
      flex: 'none',
      height: 'auto'
    },
    '& .rbc-month-row': {
      minHeight: 'calc((100vw - 280px) / 7)',
      flex: '1 1 auto',
      flexBasis: 'auto'
    },
    '& .rbc-month-header': {
      border: 'none',
      minHeight: '28px'
    },
    '& .rbc-month-header .rbc-header': {
      padding: 0,
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90} !important`
    },
    [theme.breakpoints.down('xs')]: {
      // Constrain the mobile-month Card height too (was 'auto') so the event
      // list inside the bottom panel scrolls within the Card and the
      // SalesCalendarFooter stays pinned at the bottom.
      height: 'max(calc(100dvh - 200px), 600px)',
      paddingBottom: ({ view, tab }) => (tab === 'map' || view === 'day' || view === 'week' ? 0 : theme.spacing(3))
    },
    '& .rbc-allday-cell': {
      display: ({ hideAllDayCell }) => (hideAllDayCell ? 'none' : 'block'),
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      // Desktop week view: shift the all-day backgrounds + events right past
      // the gutter so they sit roughly above their day columns. Slightly less
      // than the gutter's 61px so the cards visually nudge into their column.
      // Mobile: gutter is in flex flow already; small inset nudges cards a
      // few px right so they line up with the day-slot below the time gutter.
      paddingLeft: ({ view, isXs }) => {
        if (view !== 'week') return 0
        return isXs ? '4px' : '57px'
      },
      boxSizing: 'border-box',
      // Anchor the all-day row's bottom separator to this cell so the line
      // stays visible regardless of time-grid scroll state (the parent
      // .rbc-time-header's borderBottom alone gets clobbered by RBC's
      // .rbc-overflowing class when the grid becomes scrollable).
      borderBottom: ({ hideAllDayCell }) =>
        hideAllDayCell ? 'none' : `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      '& .rbc-day-bg': {
        backgroundColor: 'transparent !important',
        outline: NONE_IMPORTANT
      },
      '& .rbc-row-segment': {
        // Match the tight top edge in image #8 — symmetric zero-padding so a
        // single event row reads as equal top/bottom. Inter-row spacing for
        // stacked all-day events is handled by .rbc-row + .rbc-row below.
        paddingTop: 0,
        paddingBottom: 0,
        // Small horizontal inset so the event pill doesn't sit flush against
        // its day column's left/right borders.
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5)
      },
      '& .rbc-row-content': {
        // The generic .rbc-row-content rule below sets vertical margins for
        // month-view rows; zero them inside the all-day strip so a single
        // event row doesn't render with ~16px of trailing whitespace.
        marginTop: 0,
        marginBottom: 0,
        // RBC default sets min-height: 20px on every .rbc-row inside the
        // time view, which adds blank space above the events when the
        // content is shorter. Drop it here.
        '& .rbc-row': {
          minHeight: 0
        },
        // Slight gap between stacked all-day rows without adding edge padding
        // around a single row.
        '& .rbc-row + .rbc-row': {
          marginTop: '2px'
        },
        '& .rbc-row:empty': {
          height: 0,
          minHeight: 0
        }
      }
    },
    '& .rbc-allday-cell .rbc-row-bg .rbc-day-bg': {
      border: NONE_IMPORTANT
    },
    '& .rbc-time-header': {
      border: 'none',
      // Mobile only: separator between the toolbar (Today/date) and the
      // day-name row. Desktop has enough breathing room without it.
      borderTop: ({ isXs }) => (isXs ? `1px solid ${theme.paintscout.palette.brand.Neutral.P90}` : 'none'),
      position: 'relative',
      // Sits above the scrollable time-content so time-grid events
      // (z-index 1001) can't leak above the scroll clip line on top of
      // the day-name headers. Background must be opaque or scrolled
      // events would show through.
      zIndex: 1200,
      backgroundColor: theme.palette.common.white,
      '& > *': {
        border: 'none'
      },
      // Separator under the day-name row in week view, so the day headers sit
      // in their own band above the all-day strip. The border lives on each
      // header cell rather than the row so today's white background (set on
      // .rbc-today) can't cover it.
      '& .rbc-row.rbc-time-header-cell': {
        // Day view already shows the date in the toolbar, so hide this row
        // there. Week view keeps it for the column day labels.
        display: ({ view }) => (view === 'day' ? 'none' : 'flex'),
        minHeight: '43px',
        // Align day-name columns with the time grid below by insetting them
        // past the 60px time gutter (+1px gutter border). Mobile keeps the
        // gutter in flex flow, so no inset is needed there.
        paddingLeft: ({ view, isXs }) => {
          if (view !== 'week') return 0
          return isXs ? 0 : '61px'
        }
      },
      '& .rbc-row.rbc-time-header-cell .rbc-header': {
        borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90} !important`,
        padding: theme.spacing(1.125, 1.5, 1.25, 1.5)
      },
      // In day view, the toolbar already shows the date and there's no
      // all-day events row, so hide the redundant header band that would
      // otherwise sit between the toolbar and the time grid. Week view
      // always shows the day-name row so users see the date columns even
      // with no all-day events.
      display: ({ view, hideAllDayCell }) => (view === 'day' && hideAllDayCell ? 'none' : 'flex')
      // The all-day row's bottom separator lives on `.rbc-allday-cell` and
      // `.rbc-time-header-gutter` so it survives RBC toggling .rbc-overflowing
      // on scroll. Doing it here too would double-draw at scroll=0.
    },
    // Week view: pull the "All day" gutter out of the flex flow and pin it
    // to the bottom-left of the time-header so the day-name row spans full
    // width across the top and the gutter sits only beside the all-day row.
    // Day view: keep the default in-flow flex layout so the gutter sits next
    // to the all-day events horizontally without overlap.
    '& .rbc-time-header-gutter': {
      // Always render the gutter on week view (even with no all-day events)
      // so its borderTop fills the gap below the day-name row. Day view still
      // hides it when there are no all-day events.
      display: ({ view, hideAllDayCell }) => {
        if (view === 'week') return 'flex'
        return hideAllDayCell ? 'none' : 'flex'
      },
      alignItems: 'center',
      justifyContent: 'center',
      // Desktop week view: absolute over the all-day strip area, so the
      // day-name row above can span full width over the gutter.
      // Mobile week view: sticky in flex flow so the gutter pins to the
      // viewport's left edge during horizontal scroll. Takes 60px of flex
      // space, which insets the day-name row + day-slots together.
      position: ({ view, isXs }) => {
        if (view !== 'week') return 'static'
        return isXs ? 'sticky' : 'absolute'
      },
      left: ({ view }) => (view === 'week' ? 0 : undefined),
      top: ({ view, isXs }) => (view === 'week' && !isXs ? '43px' : undefined),
      bottom: ({ view, isXs }) => (view === 'week' && !isXs ? 0 : undefined),
      height: ({ view }) => (view === 'week' ? 'auto' : undefined),
      width: '61px !important',
      minWidth: '61px !important',
      padding: theme.spacing(0, 1.5),
      // Week view (desktop AND mobile): top border separates the gutter from
      // the day-name row above. Day view has no day-name row (the toolbar
      // already shows the date), so the toolbar's own borderBottom is right
      // above the gutter — drop the gutter's borderTop there to avoid a
      // double line.
      borderTop: ({ view }) =>
        view === 'week' ? `1px solid ${theme.paintscout.palette.brand.Neutral.P90} !important` : 'none',
      borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P90} !important`,
      // Continues the all-day row's bottom separator across the gutter so the
      // line is solid regardless of scroll state. !important matches the
      // borderTop/borderRight pattern — the parent's `& > *: border: none`
      // reset (line ~167) would otherwise strip it.
      borderBottom: ({ view, hideAllDayCell }) =>
        view !== 'month' && !hideAllDayCell
          ? `1px solid ${theme.paintscout.palette.brand.Neutral.P90} !important`
          : 'none',
      color: theme.paintscout.palette.brand.Neutral.P60,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 400,
      // Mobile week view: 40px top margin pushes the sticky gutter below the
      // day-name row so the day labels can scroll nicely above it on
      // horizontal pan. Matches the row's minHeight + padding total.
      marginTop: ({ view, isXs }) => (isXs && view === 'week' ? '43px' : 0),
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      // 1px on week view + hideAllDayCell so the borderTop renders even when
      // the all-day strip collapses to 0 height.
      minHeight: ({ view, hideAllDayCell }) => {
        if (view === 'week') return hideAllDayCell ? '1px' : 0
        return '40px'
      },
      // Mobile sticky: above .rbc-event (z-index 1001) so all-day events
      // sliding behind the sticky gutter don't bleed over the "All day" label.
      zIndex: ({ isXs }) => (isXs ? 1100 : 2),
      backgroundColor: theme.palette.common.white
    },
    '& .rbc-time-header-content': {
      // Desktop week view: full width so the day-name row spans over the
      // absolute-positioned all-day gutter.
      // Mobile week view: auto so it shares flex space with the in-flow
      // sticky gutter (gutter takes ~61px, content gets the rest, and the
      // day-name row + all-day events line up with the time grid below).
      // Force flex-column so the day-name row stays visible even when the
      // all-day cell is hidden.
      display: 'flex',
      flexDirection: 'column',
      width: ({ view, isXs }) => {
        if (view !== 'week') return
        return isXs ? 'auto' : '100%'
      }
    },
    '& .rbc-time-header.rbc-overflowing': {
      borderRight: 'none',
      // RBC sets margin-right inline to match the time-content scrollbar
      // width, which leaves a visible gap on the right of the header. Force
      // it back to 0 so the header spans the full width.
      marginRight: ZERO_IMPORTANT,
      borderTop: 'none'
    },
    '& .rbc-header': {
      border: NONE_IMPORTANT,
      height: 'fit-content'
    },
    '& .rbc-today': {
      backgroundColor: theme.palette.common.white
    },
    '& .rbc-button-link': {
      width: '100%'
    },
    '& .rbc-month-view .rbc-day-bg': {
      position: 'relative',
      flex: '1 1 0',
      minWidth: 0,
      boxSizing: 'border-box',
      borderColor: theme.paintscout.palette.brand.Neutral.P90,
      backgroundColor: theme.palette.common.white,
      cursor: 'pointer',
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: theme.paintscout.palette.brand.Neutral.P96
        }
      },
      '&:first-child': { borderLeft: 'none' },
      '&:last-child': { borderRight: 'none' }
    },
    '& .rbc-month-view .rbc-day-bg.rbc-today': {
      outline: NONE_IMPORTANT
    },
    '& .rbc-day-bg.disabled-day': {
      pointerEvents: 'none'
    },
    '& .rbc-row-content': {
      pointerEvents: 'none',
      // Use vertical margin only — horizontal margin would shrink the
      // date-cell / event row relative to the .rbc-row-bg cells below,
      // causing the columns to drift out of alignment toward Sat.
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5)
    },
    '& .rbc-events-container': {
      margin: '0 !important',
      top: '-3px'
    },
    '& .rbc-timeslot-group': {
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      minHeight: '88px',
      maxHeight: '88px'
    },
    '& .rbc-time-slot': {
      border: 'none !important',
      margin: 0,
      backgroundColor: 'transparent',
      cursor: 'pointer',
      zIndex: 1000,
      borderRadius: 0,
      [theme.breakpoints.up('md')]: {
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            backgroundColor: `${theme.paintscout.palette.brand.Neutral.P98} !important`
          }
        }
      }
    },
    // Top half covered — draw dashed border only around the bottom half on hover
    '& .rbc-time-slot.rbc-time-slot-covered-top': {
      marginTop: '49px !important'
    },
    // Bottom half covered — draw dashed border only around the top half on hover
    '& .rbc-time-slot.rbc-time-slot-covered-bottom': {
      maxHeight: '33px !important'
    },
    '& .rbc-time-content': {
      border: 'none'
    },
    // Vertical separators between each day column in the time grid + the
    // matching all-day row cells.
    '& .rbc-time-content > .rbc-day-slot + .rbc-day-slot': {
      borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P90} !important`
    },
    '& .rbc-day-slot.rbc-time-column': {
      '& .rbc-timeslot-group:first-child': {
        '& .rbc-time-slot': {
          marginTop: ZERO_IMPORTANT
        }
      }
    },
    '& .rbc-row-segment': {
      padding: theme.spacing(0.5, 0.75)
    },
    '& .rbc-time-gutter': {
      pointerEvents: 'none',
      borderRadius: 0,
      borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      backgroundColor: theme.palette.common.white,
      color: theme.paintscout.palette.brand.Neutral.P60,
      margin: 0,
      padding: 0,
      whiteSpace: 'nowrap',
      // Match the absolute-positioned .rbc-time-header-gutter width in week
      // view so the all-day strip's inset (paddingLeft on .rbc-allday-cell)
      // lines up with these day-slot columns.
      width: '60px',
      minWidth: '60px',
      flex: '0 0 60px',
      boxSizing: 'border-box',
      '& .rbc-timeslot-group': {
        fontWeight: 400,
        margin: ZERO_IMPORTANT,
        border: 'none',
        overflow: 'visible',
        '& .rbc-time-slot': {
          margin: ZERO_IMPORTANT,
          backgroundColor: 'transparent !important',
          padding: theme.spacing(0, 1.5),
          textAlign: 'right',
          overflow: 'visible',
          '& .rbc-timeslot-period, & .rbc-label': {
            fontSize: theme.typography.pxToRem(12),
            color: theme.paintscout.palette.brand.Neutral.P60,
            whiteSpace: 'nowrap',
            display: 'inline-block',
            // Sit centered on the hour gridline (the top border of this group)
            transform: 'translateY(-50%)'
          }
        }
      },
      [theme.breakpoints.down('xs')]: {
        '& .rbc-timeslot-group .rbc-time-slot': {
          padding: theme.spacing(0, 0.75)
        }
      }
    },
    '& .rbc-day-slot .rbc-timeslot-group': {
      borderBottom: ({ isXs, view }) =>
        isXs && view === 'week' ? 'none' : `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    // Drop the bottom border on the very last hour row so it doesn't double
    // up with the Card's bottom edge.
    '& .rbc-day-slot .rbc-timeslot-group:last-child, & .rbc-time-gutter .rbc-timeslot-group:last-child, & .rbc-day-slot > .rbc-timeslot-group:nth-last-child(1)':
      {
        borderBottom: 'none !important'
      },
    '& .rbc-day-slot, & .rbc-time-column': {
      borderBottom: 'none !important',
      borderLeft: 'none !important',
      borderRight: 'none !important'
    },
    // Hide the very top time label — it gets pulled up by translateY(-50%)
    // and squished against the toolbar / all-day row.
    '& .rbc-time-gutter .rbc-timeslot-group:first-child .rbc-time-slot:first-child .rbc-timeslot-period, & .rbc-time-gutter .rbc-timeslot-group:first-child .rbc-time-slot:first-child .rbc-label':
      {
        visibility: 'hidden'
      },
    '& .rbc-current-time-indicator': {
      display: 'none'
    },
    '& .rbc-event': {
      padding: 0,
      backgroundColor: 'transparent',
      border: NONE_IMPORTANT,
      zIndex: 1001,
      height: '20px',
      pointerEvents: 'auto',
      transform: ({ view }) => `translateY(${view === 'day' ? 2 : view === 'week' ? 3 : 0}px)`,
      '& .rbc-event-label': {
        display: 'none '
      },
      '&:focus': {
        outline: 'none'
      },
      '&:has(.rbc-event-unavailable)': {
        width: '100% !important',
        overflow: 'visible'
        // paddingTop: '4px'
      }
    },

    '& .rbc-row-bg': {
      '& .rbc-day-bg:first-child': {
        display: ({ hideWeekends, view }) => hideWeekends && view !== 'week' && 'none'
      },
      '& .rbc-day-bg:last-child': {
        display: ({ hideWeekends, view }) => hideWeekends && view !== 'week' && 'none'
      }
    },
    '& .rbc-row': {
      '& .rbc-date-cell:first-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      },
      '& .rbc-date-cell:last-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      }
    },
    '& .rbc-date-cell': {
      pointerEvents: 'none',
      // Match .rbc-day-bg's flex distribution + box-sizing so the date
      // numbers align with their column boundaries (including the 1px
      // cell borders) instead of drifting toward the right.
      flex: '1 1 0',
      minWidth: 0
      // boxSizing: 'border-box'
    },
    // Tighten the row that holds the date numbers in month view — RBC
    // defaults .rbc-row min-height to 20px, which leaves empty space
    // between the date number and the first event row.
    '& .rbc-month-view .rbc-row-content .rbc-row:first-child': {
      minHeight: 10
    },
    '& .rbc-row.rbc-month-header': {
      minHeight: 32,
      '& .rbc-header': {
        padding: 0,
        flex: '1 1 0',
        minWidth: 0,
        boxSizing: 'border-box'
      },
      '& .rbc-header:first-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      },
      '& .rbc-header:last-child': {
        display: ({ hideWeekends }) => hideWeekends && 'none'
      }
    },
    '& .rbc-event-allday': {
      backgroundColor: 'transparent',
      // !important so this definitively beats the 20px height on .rbc-event
      // (same specificity, so without !important order alone determines the
      // winner). Lets the all-day strip's height be driven only by stacked
      // event rows.
      height: 'fit-content !important',
      transform: 'translateY(0) !important',
      overflow: 'hidden'
    },
    '& .rbc-slot-selection': {
      display: 'none'
    },
    // Mobile week view: horizontal scroll with the time gutter pinned on the
    // left. .rbc-time-view becomes the single scroll context (both axes) so
    // the day-name header and the time grid scroll together horizontally.
    [theme.breakpoints.down('xs')]: {
      '& .rbc-time-view': {
        overflow: ({ view }) => (view === 'week' ? 'auto' : undefined)
      },
      // Default RBC sets overflow-y: auto on .rbc-time-content which captures
      // horizontal scroll inside it (CSS coerces overflow-x to auto when y is
      // not visible) — that desyncs the header. Force visible so the parent
      // .rbc-time-view handles both axes.
      '& .rbc-time-content': {
        overflow: ({ view }) => (view === 'week' ? 'visible' : 'auto'),
        // 60px sticky time gutter + (hideWeekends ? 5 : 7) day columns × 100px;
        // hardcoding 760 left dead horizontal scroll past the last weekday when weekends were hidden.
        minWidth: ({ view, hideWeekends }) => (view === 'week' ? (hideWeekends ? '560px' : '760px') : 0)
      },
      '& .rbc-time-header': {
        minWidth: ({ view, hideWeekends }) => (view === 'week' ? (hideWeekends ? '560px' : '760px') : 0)
      },
      // Important: target .rbc-day-slot only — the .rbc-time-gutter element
      // also has the .rbc-time-column class, and would otherwise expand to
      // 100px instead of staying at the 60px gutter width.
      // Explicit defaults for non-week so the lone day-slot isn't stuck at
      // the week view's 100px width when switching back to day view.
      '& .rbc-time-content > .rbc-day-slot': {
        minWidth: ({ view }) => (view === 'week' ? '100px' : 0),
        flex: ({ view }) => (view === 'week' ? '0 0 100px' : '1 0 0%')
      },
      '& .rbc-time-header-content .rbc-time-header-cell .rbc-header': {
        minWidth: ({ view }) => (view === 'week' ? '100px' : 0),
        flex: ({ view }) => (view === 'week' ? '0 0 100px' : '1 0 0%')
      },
      '& .rbc-allday-cell .rbc-row-bg .rbc-day-bg, & .rbc-allday-cell .rbc-row-content .rbc-row-segment': {
        minWidth: ({ view }) => (view === 'week' ? '100px' : 0)
      },
      '& .rbc-time-gutter': {
        position: ({ view }) => (view === 'week' ? 'sticky' : undefined),
        left: ({ view }) => (view === 'week' ? 0 : undefined),
        // Higher than .rbc-event (z-index 1001) so events sliding behind the
        // sticky gutter on horizontal scroll don't bleed over the time labels.
        zIndex: ({ view }) => (view === 'week' ? 1100 : undefined)
      }
    }
  },
  fixedFooter: ({ sideNavWidth }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar + 1,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 -2px 4px rgba(0,0,0,0.06)',
    paddingLeft: sideNavWidth,
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  }),
  fixedFooterSpacer: {
    height: 60,
    [theme.breakpoints.down('xs')]: {
      height: 110
    }
  }
}))

type MapGranularity = 'day' | 'week' | 'month'

let latestMapGranularity: MapGranularity = 'day'

export default function Calendar() {
  const {
    date,
    setDate,
    tab,
    loading,
    view,
    setView,
    hideWeekends,
    events,
    allEvents,
    selectedUsers,
    addEvent,
    updateEvent,
    deleteEvent
  } = useCalendar()
  const location = useLocation()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const localizer = momentLocalizer(moment)
  const mobile = useIsMobile({ xs: true })
  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('md'))
  const { sideNavWidth } = useNavContext()
  const { user } = useUser()
  const { permissionToViewAllEvents } = useUserPermissions()
  const { users } = useUsers()
  const { openDialog, dismissAllDialogs, stack: dialogStack } = useDialogs()

  const isMultiUsersMonthView =
    view === 'month' && users && users.length > 1 && selectedUsers.length > 1 && permissionToViewAllEvents
  const isMonthViewOnSmallScreen = view === 'month' && isSmallScreen

  const mapGranularity: MapGranularity =
    view === 'day' || view === 'week' || view === 'month'
      ? (view as MapGranularity)
      : view === 'work_week'
        ? 'week'
        : 'month'
  latestMapGranularity = mapGranularity

  const calendarView = tab === 'map' ? 'map' : view

  const eventsToDisplay = isMultiUsersMonthView || isMonthViewOnSmallScreen ? groupEventsByEstimator(events) : events

  const unavailableEvents = useMemo(
    () => eventsToDisplay.filter((e) => e.eventType === '_unavailable_'),
    [eventsToDisplay]
  )

  /**
   * Compute calendar grid bounds and trim morning unavailable strips so they
   * render per-day. Bounds use time-of-day across the visible range:
   *
   * - `calendarMin` = 1hr before the earliest morning transition (the earliest
   *   morning-unavailable end, or the earliest scheduled event start across
   *   any visible day), rounded down to the hour, clamped at 12:00am.
   * - `calendarMax` = 1hr after the latest evening transition (the latest
   *   evening-unavailable start or the latest event end), rounded up to the
   *   hour, clamped at 11:59pm.
   *
   * Morning before-business events (start at midnight) are collected and
   * re-emitted with their start clamped to calendarMin's time-of-day on
   * their own day so each visible day shows its own grey strip.
   *
   * Exceptions:
   * - A full-day unavailable cover is rendered as-is (no morning trim).
   * - If the selected day is fully unavailable, calendarMin/Max are dropped
   *   so the grid doesn't collapse around the cover.
   */
  const { eventsForGrid, calendarMin, calendarMax } = useMemo(() => {
    const dayStart = moment(date).startOf('day')
    const isWeekView = view === 'week'
    const rangeStart = isWeekView
      ? hideWeekends
        ? moment(date).startOf('week').add(1, 'day')
        : moment(date).startOf('week')
      : dayStart.clone()
    const rangeEnd = isWeekView
      ? hideWeekends
        ? moment(date).endOf('week').subtract(1, 'day')
        : moment(date).endOf('week')
      : moment(date).endOf('day')

    // Time-of-day in minutes-since-midnight. Bounds are compared by TOD so the
    // "earliest morning" / "latest evening" across the week reflect *when* in
    // the day a transition happens — not which calendar date comes first.
    const todMinutes = (m: moment.Moment) => m.hour() * 60 + m.minute()

    let hasFullDayUnavailable = false
    const morningEvents: { eEnd: moment.Moment; original: CalendarEvent }[] = []
    const trimmedEvents: CalendarEvent[] = []
    const morningTODs: number[] = []
    const eveningTODs: number[] = []

    eventsToDisplay.forEach((e) => {
      const eStart = moment(e.start)
      const eEnd = moment(e.end)
      // Invalid Date in start/end (eg. an optimistic event mid-add, a bad
      // GCal/Zapier import) makes todMinutes return NaN and poisons every
      // Math.min/max/ceil downstream — RBC then calls new Array(NaN) and
      // throws RangeError. Pass the event through to RBC unchanged but keep
      // it out of the bounds math.
      if (!eStart.isValid() || !eEnd.isValid()) {
        trimmedEvents.push(e)
        return
      }
      const inRange = eEnd.isAfter(rangeStart) && eStart.isBefore(rangeEnd)

      if (e.eventType !== '_unavailable_') {
        trimmedEvents.push(e)
        // Drive grid bounds from scheduled events too, so anything outside
        // the user's availability stays visible with a 1-hour buffer.
        // Require eEnd >= eStart so a single negative-duration event can't
        // invert min/max — that would make RBC compute numGroups < 0 and
        // throw RangeError from new Array(negative) in getSlotMetrics.
        if (!e.allDay && inRange && eStart.isSame(eEnd, 'day') && eEnd.isSameOrAfter(eStart)) {
          morningTODs.push(todMinutes(eStart))
          eveningTODs.push(todMinutes(eEnd))
        }
        return
      }
      const startsAtMidnight = eStart.hour() === 0 && eStart.minute() === 0
      const endsAtMidnight = eEnd.hour() === 0 && eEnd.minute() === 0

      // Full-day unavailable: spans 00:00 → ~23:59 of one day. Render as a
      // cover so the day shows a striped block of "no availability".
      const eventEndOfDay = moment(eStart).endOf('day')
      const endsAtEndOfDay = eEnd.isSameOrAfter(moment(eventEndOfDay).subtract(1, 'minute'), 'minute')
      const isFullDayUnavailable = startsAtMidnight && eEnd.isSame(eStart, 'day') && endsAtEndOfDay
      if (isFullDayUnavailable) {
        trimmedEvents.push(e)
        // If it covers the selected day, bypass min/max so day view's grid
        // doesn't collapse to a 1-hour strip.
        if (eStart.isSameOrBefore(dayStart, 'minute') && eEnd.isSame(dayStart, 'day')) {
          hasFullDayUnavailable = true
        }
        return
      }

      if (startsAtMidnight) {
        // Before-business buffer — defer rendering; we'll re-emit it
        // trimmed to calendarMin so each visible day shows its own strip.
        if (eEnd.isAfter(rangeStart) && eEnd.isSameOrBefore(rangeEnd)) {
          morningEvents.push({ eEnd, original: e })
          morningTODs.push(todMinutes(eEnd))
        }
        return
      }

      // After-business strip — either ending at 23:59 of the same day
      // (getAvailabilityGaps output) or at next-day midnight (external
      // imports). Renders as a tile and drives the evening calendarMax.
      const isEveningUnavailable = inRange && (endsAtMidnight || (eStart.isSame(eEnd, 'day') && endsAtEndOfDay))
      if (isEveningUnavailable) {
        eveningTODs.push(todMinutes(eStart))
        if (!endsAtMidnight) {
          trimmedEvents.push(e)
        }
        return
      }
      trimmedEvents.push(e)
    })

    if (hasFullDayUnavailable) {
      return { eventsForGrid: trimmedEvents, calendarMin: undefined, calendarMax: undefined }
    }

    const earliestMorningTOD = morningTODs.length ? Math.min(...morningTODs) : null
    const latestEveningTOD = eveningTODs.length ? Math.max(...eveningTODs) : null

    // 1-hour buffer before the earliest morning transition, rounded down to
    // the hour, clamped at 12:00am so we never spill into the previous day.
    const minHour = earliestMorningTOD !== null ? Math.max(0, Math.floor(earliestMorningTOD / 60) - 1) : null
    // 1-hour buffer after the latest evening transition, rounded up to the
    // hour, clamped at 11:59pm so we never spill into the next day.
    let maxHour: number | null = null
    let maxMinute = 0
    if (latestEveningTOD !== null) {
      const buffered = latestEveningTOD + 60
      // Clamp anything that would ceil past hour 23 — without this, an event
      // ending at 22:01–22:58 produces buffered in (1380, 1438], maxHour=24,
      // and moment's .hour(24) wraps `max` to next-day midnight.
      if (buffered > 23 * 60) {
        maxHour = 23
        maxMinute = 59
      } else {
        maxHour = Math.ceil(buffered / 60)
      }
    }

    const min =
      minHour !== null ? rangeStart.clone().hour(minHour).minute(0).second(0).millisecond(0).toDate() : undefined
    const max =
      maxHour !== null
        ? rangeStart.clone().hour(maxHour).minute(maxMinute).second(0).millisecond(0).toDate()
        : undefined

    // Backstop: if a bad event still slipped through and inverted the bounds,
    // fall back to RBC's defaults rather than letting it allocate a negative-
    // length array in getSlotMetrics.
    if (min && max && min >= max) {
      return { eventsForGrid: trimmedEvents, calendarMin: undefined, calendarMax: undefined }
    }

    if (min) {
      const minMoment = moment(min)
      const minTOD = todMinutes(minMoment)
      morningEvents.forEach(({ eEnd, original }) => {
        // TOD comparison: a strip on any day stays in scope as long as its
        // end-of-day-time is past calendarMin's time-of-day. Comparing as
        // absolute instants would drop strips on days chronologically
        // before the day from which `min` was derived.
        if (todMinutes(eEnd) > minTOD) {
          // Clamp to *this* day's calendarMin time so each morning buffer
          // stays a single-day event. Using minMoment directly would make
          // every day past the first span Mon 7am → that day's eEnd, which
          // RBC then places in the all-day strip as a multi-day event.
          const dayStart = moment(original.start)
            .hour(minMoment.hour())
            .minute(minMoment.minute())
            .second(minMoment.second())
            .millisecond(minMoment.millisecond())
          trimmedEvents.push({ ...original, start: dayStart.toDate() })
        }
      })
    }

    return { eventsForGrid: trimmedEvents, calendarMin: min, calendarMax: max }
  }, [eventsToDisplay, date, view, hideWeekends])
  // Shift each event's start/end so its *local* fields (year/month/day/hour/min)
  // match the company-tz reading. react-big-calendar positions events in the time
  // grid using `getHours()` etc. on the raw Date — which uses the *browser's* tz —
  // so without this shift an event scheduled for 9am company-tz renders at the
  // browser-tz offset hour. Click handlers must look up the original event from
  // `useCalendar().events` (by id) before opening dialogs, so dialogs continue
  // to format with explicit `.tz(timezone)` and see real UTC instants.
  const eventsForGridDisplay = useMemo(() => {
    return eventsForGrid.map((e) => {
      // `_unavailable_` events come from getAvailabilityGaps in browser-local
      // time already; re-shifting via toCalendarLocalDate can push their end
      // across the next calendar day when browser tz differs from company tz,
      // which makes RBC route them into the all-day strip. Leave them alone.
      if (e.eventType === '_unavailable_') return e
      const start = toCalendarLocalDate(e.start, timezone) ?? e.start
      let end = toCalendarLocalDate(e.end, timezone) ?? e.end
      if (!e.allDay && moment(end).diff(moment(start), 'minutes') < MIN_EVENT_DISPLAY_MINUTES) {
        end = moment(start).add(MIN_EVENT_DISPLAY_MINUTES, 'minutes').toDate()
      }
      return { ...e, start, end }
    })
  }, [eventsForGrid, timezone])
  // Detect against `eventsForGridDisplay` (the timezone-shifted list RBC
  // actually receives) and mirror RBC's all-day-strip rule (allDay flag OR
  // cross-day span) so the gutter label tracks what's actually rendered —
  // including multi-day `_unavailable_` blocks and GCal imports without the
  // allDay flag.
  const hasAllDayEvents = useMemo(() => {
    if (view === 'month') return false
    if (!eventsForGridDisplay?.length) return false
    let rangeStart = moment(date).startOf(view === 'week' ? 'week' : 'day')
    let rangeEnd = moment(date).endOf(view === 'week' ? 'week' : 'day')
    if (view === 'week' && hideWeekends) {
      rangeStart = rangeStart.add(1, 'day')
      rangeEnd = rangeEnd.subtract(1, 'day')
    }
    return eventsForGridDisplay.some((event) => {
      const eventStart = moment(event.start)
      const eventEnd = event.end ? moment(event.end) : eventStart
      // Exact-midnight end is RBC's exclusive boundary, not a presence on the
      // next day — applies equally to GCal-style all-day shape
      // ([00:00 X, 00:00 X+1)) and to timed events that end on the hour at
      // midnight. Without this, the all-day gutter label shows on the day after
      // a GCal all-day event even though RBC correctly omits the event there.
      const isExactMidnight = (m: moment.Moment) =>
        m.hours() === 0 && m.minutes() === 0 && m.seconds() === 0 && m.milliseconds() === 0
      const normalizedEnd = isExactMidnight(eventEnd) ? eventEnd.clone().subtract(1, 'millisecond') : eventEnd
      const isAllDayLike = event.allDay || !eventStart.isSame(normalizedEnd, 'day')
      if (!isAllDayLike) return false
      return normalizedEnd.isSameOrAfter(rangeStart) && eventStart.isBefore(rangeEnd)
    })
  }, [eventsForGridDisplay, date, view, hideWeekends])
  // NOTE: RBC caches the `components.timeGutterHeader` reference at mount and
  // doesn't reliably re-render it when our state changes — the function fires
  // (you can log inside it) but the returned JSX never reaches the DOM. Pass a
  // no-op component and inject the "All day" label via direct DOM write in the
  // useLayoutEffect below, keyed off `hasAllDayEvents` so it tracks state.
  const TimeGutterHeader = useMemo(() => {
    function TimeGutterHeader() {
      return null
    }
    return TimeGutterHeader
  }, [])
  const calendarRootRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const root = calendarRootRef.current
    if (!root) return
    const gutter = root.querySelector<HTMLElement>('.rbc-time-header-gutter')
    if (!gutter) return
    const desired = view !== 'month' && hasAllDayEvents ? 'All day' : ''
    if (gutter.textContent !== desired) gutter.textContent = desired
  })
  const classes = useStyles({
    view,
    tab,
    hideWeekends: hideWeekends && view === 'week',
    hideAllDayCell: !hasAllDayEvents,
    isXs: mobile,
    sideNavWidth
  })
  const handleSetView = (newView: View | 'map') => {
    if (newView === 'map') {
      return
    }

    setView(newView)
  }

  const getDayEvents = (date: Date) => {
    return events.filter((event) => {
      const eventStart = moment(event.start)
      const eventEnd = moment(event.end)
      const targetDate = moment(date)

      return (
        // The target date is the same or after the event start (so the event has started)
        targetDate.isSameOrAfter(eventStart, 'day') &&
        // The target date is before the event end (so the event is ongoing)
        (targetDate.isBefore(eventEnd, 'day') ||
          // Or the target date is the same as the event end,
          (targetDate.isSame(eventEnd, 'day') &&
            // but only include if the event does NOT end exactly at midnight (00:00:00.000)
            !(
              eventEnd.hour() === 0 &&
              eventEnd.minute() === 0 &&
              eventEnd.second() === 0 &&
              eventEnd.millisecond() === 0
            )))
      )
    })
  }

  const buildNewEvent = (start?: Date, end?: Date): Partial<CalendarEvent> => {
    const estimator =
      selectedUsers?.length === 1
        ? {
            id: selectedUsers[0].user_id,
            name: selectedUsers[0].name,
            color: selectedUsers[0].user_metadata?.color,
            email: selectedUsers[0].email
          }
        : {
            id: user?.user_id,
            name: user?.name,
            color: user?.user_metadata?.color,
            email: user?.email
          }
    return {
      id: uuid(),
      start,
      end,
      estimator
    }
  }

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    if (slotInfo.slots.length > 2 || dialogStack.length > 0) {
      return
    }

    if (view === 'month') {
      onMonthSlotClicked(slotInfo.start || slotInfo?.slots?.[0])
      return
    }

    const { start, end } = getEventTimes(slotInfo.start || slotInfo?.slots?.[0], view, timezone)

    if (moment(start).isBefore(moment(), 'minute')) {
      return
    }

    onSlotClicked(buildNewEvent(start, end))
  }

  const handleDirectSlotClick = (date: Date, coverage?: 'top' | 'bottom') => {
    if (dialogStack.length > 0) {
      return
    }
    if (view === 'month') {
      onMonthSlotClicked(date)
      return
    }
    const { start, end } = getEventTimes(date, view, timezone)
    let finalStart = start
    let finalEnd = end
    if (coverage === 'bottom') {
      // Bottom half covered — clickable area is :00 to :30
      finalEnd = moment(start).add(30, 'minutes').toDate()
    } else if (coverage === 'top') {
      // Top half covered — clickable area is :30 to end of hour
      finalStart = moment(start).add(30, 'minutes').toDate()
    }
    if (moment(finalStart).isBefore(moment(), 'minute')) {
      return
    }

    onSlotClicked(buildNewEvent(finalStart, finalEnd))
  }

  const onSlotClicked = (newEvent: Partial<CalendarEvent>, date?: Date) => {
    openDialog(EventDialog, {
      event: newEvent,
      date,
      isNew: true,
      selectedUsers: view === 'month' ? selectedUsers : undefined,
      onConfirm: (event: CalendarEvent, eventTemplate: string) => {
        addEvent(event, eventTemplate)
        dismissAllDialogs()
      }
    })
  }

  const onMonthSlotClicked = (date: Date) => {
    const dayEvents = getDayEvents(date)

    const isInPast = moment(date).isBefore(moment(), 'day')

    if (dayEvents.length === 0 && isInPast) {
      return
    }

    if (dayEvents.length === 0) {
      if (moment(date).isSame(moment(), 'day')) {
        const { start, end } = getEventTimes(date, view, timezone)
        onSlotClicked(buildNewEvent(start, end), date)
      } else {
        onSlotClicked(buildNewEvent(), date)
      }

      return
    }

    const hasMultipleEstimators =
      dayEvents.length > 1 && !dayEvents.every((event) => event.estimator.id === dayEvents[0].estimator.id)

    openDialog(ViewEventsDialog, {
      date,
      events: hasMultipleEstimators ? groupEventsByEstimator(dayEvents) : dayEvents,
      updateEvent,
      deleteEvent,
      addEvent,
      groupByEstimator: hasMultipleEstimators,
      selectedUsers
    })
  }

  // Check url for an id that we want to open in dialog
  useEffect(() => {
    const eventIdMatch = location.search.match(/&([a-z0-9-]+)\/?$/)
    if (allEvents.length && eventIdMatch && eventIdMatch[1]) {
      setUrlQuery({
        location,
        replaceParams: true,
        params: {
          tab: 'calendar',
          search: null,
          filter: null
        }
      })
      const eventId = eventIdMatch[1]
      const event = allEvents.find((e) => e.id === eventId)
      setDate(event.start)
      openDialog(EventDialog, {
        event,
        onDelete: deleteEvent,
        onConfirm: (event: CalendarEvent, eventTemplate: string) => {
          updateEvent(event, eventTemplate)
          dismissAllDialogs()
        }
      })
    }
  }, [allEvents])

  const showFooter = tab === 'calendar' && !loading && (view === 'week' || view === 'month')

  return (
    <>
      <Card ref={calendarRootRef} className={classes.root}>
        <BigCalendar<CalendarEvent>
          date={date}
          view={calendarView as unknown as View}
          onView={handleSetView}
          events={eventsForGridDisplay}
          localizer={localizer}
          timeslots={1}
          step={60}
          min={calendarMin}
          max={calendarMax}
          formats={{
            dateFormat: 'D',
            timeGutterFormat: 'h a',
            dayFormat: 'MMMM D, YYYY'
          }}
          messages={{
            allDay: 'All day',
            date: 'Date',
            time: 'Time',
            event: 'Event',
            previous: 'Back',
            next: 'Next',
            today: 'Today',
            month: 'Month',
            week: 'Week',
            day: 'Day',
            agenda: 'Agenda',
            noEventsInRange: 'There are no events in this range.',
            showMore: (total: number) => `+${total} more`
          }}
          scrollToTime={moment().toDate()}
          components={{
            toolbar: SalesCalendarToolbar,
            month: { header: MonthColumnHeader, dateHeader: MonthDateHeader, event: MonthEvent },
            week: { header: WeekColumnHeader, event: WeekEvent },
            day: { event: DayEvent },
            timeGutterHeader: TimeGutterHeader,
            showMore: ShowMore
          }}
          workdaysOnly={hideWeekends}
          dayLayoutAlgorithm={'no-overlap'}
          selectable={tab !== 'map' && view === 'month'}
          views={
            {
              day: loading ? CalendarShim : true,
              week: loading ? CalendarShim : true,
              month: loading ? CalendarShim : mobile ? MobileMonthView : true,
              map: loading ? CalendarShim : MapView
            } as Record<string, boolean | React.ComponentType<any>>
          }
          longPressThreshold={5}
          onSelectSlot={handleSelectSlot}
          onSelecting={() => false}
          slotPropGetter={(date) => {
            const slotStart = moment(date)
            const slotMid = moment(date).add(30, 'minutes')
            const slotEnd = moment(date).add(60, 'minutes')
            const topCovered = unavailableEvents.some(
              (e) => slotStart.isBefore(moment(e.end)) && slotMid.isAfter(moment(e.start))
            )
            const bottomCovered = unavailableEvents.some(
              (e) => slotMid.isBefore(moment(e.end)) && slotEnd.isAfter(moment(e.start))
            )
            const slotClass = topCovered
              ? 'rbc-time-slot-covered-top'
              : bottomCovered
                ? 'rbc-time-slot-covered-bottom'
                : undefined
            const coverage = topCovered ? 'top' : bottomCovered ? 'bottom' : undefined
            return {
              onClick: () => handleDirectSlotClick(date, coverage),
              style: moment(date).isBefore(moment()) ? { pointerEvents: 'none' } : {},
              className: slotClass
            }
          }}
          dayPropGetter={(date) => {
            if (view === 'month' && moment(date).isBefore(moment(), 'day') && getDayEvents(date).length === 0) {
              return {
                className: 'disabled-day'
              }
            }
            return {}
          }}
          onDrillDown={(date: Date, view: View) => {
            setDate(date)
            handleSetView(view)
          }}
        />
      </Card>
      {showFooter && <div className={classes.fixedFooterSpacer} aria-hidden />}
      {showFooter && (
        <div className={classes.fixedFooter}>
          <SalesCalendarFooter view={view} date={date} events={allEvents} />
        </div>
      )}
    </>
  )
}

function EventsMapView(props: any) {
  const { openDialog, dismissDialog } = useDialogs()
  const { events, updateEvent, deleteEvent, selectedUsers, view, fetchingIntegrationEvents } = useCalendar()
  const { users } = useUsers()
  const cancelEventFlow = useCancelEventFlow()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)

  const handleMarkerClick = (event: CalendarEvent, e?: React.MouseEvent<HTMLElement>) => {
    setSelectedEvent(event)
    setAnchorPosition({ top: e?.clientY ?? 0, left: e?.clientX ?? 0 })
  }

  const closePopper = () => {
    setAnchorPosition(null)
    setSelectedEvent(null)
  }

  const handleEditEvent = (event: CalendarEvent) => {
    closePopper()
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
      }
    })
  }

  const handleCancelEvent = (event: CalendarEvent) => {
    closePopper()
    cancelEventFlow(event, deleteEvent)
  }

  const selectedEstimators = selectedUsers.map((user) => user.user_id)
  const date = props.date
  const timeSpan = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'
  const filteredEvents = events.filter((event) => {
    const eventDate = moment(event.start).isSame(date, timeSpan)
    const isInSelectedEstimators = selectedEstimators.includes(event.estimator?.id)
    return eventDate && isInSelectedEstimators
  })

  const isDayView = view === 'day'
  const livePopperEvent = selectedEvent ? events.find((e) => e.id === selectedEvent.id) ?? selectedEvent : null

  return (
    <>
      <EventsMap
        {...props}
        dayView={isDayView}
        events={filteredEvents}
        onMarkerClick={handleMarkerClick}
        loading={fetchingIntegrationEvents}
        disableTooltip
      />
      {livePopperEvent && anchorPosition && (
        <EventPreviewPopper
          event={livePopperEvent}
          anchorEl={
            {
              getBoundingClientRect: () => ({
                top: anchorPosition.top,
                left: anchorPosition.left,
                right: anchorPosition.left,
                bottom: anchorPosition.top,
                width: 0,
                height: 0,
                x: anchorPosition.left,
                y: anchorPosition.top,
                toJSON: () => ({
                  top: anchorPosition.top,
                  left: anchorPosition.left,
                  right: anchorPosition.left,
                  bottom: anchorPosition.top,
                  width: 0,
                  height: 0,
                  x: anchorPosition.left,
                  y: anchorPosition.top
                })
              }),
              clientWidth: 0,
              clientHeight: 0
            } as unknown as HTMLElement
          }
          setAnchorEl={() => {}}
          users={users}
          onClose={closePopper}
          onEditEvent={handleEditEvent}
          onCancelEvent={handleCancelEvent}
          onNavigate={(path) => history.push(path)}
        />
      )}
    </>
  )
}

const MapView = (props) => <EventsMapView {...props} />
MapView.title = (date: Date) => getMapTitle(date, latestMapGranularity)
MapView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, latestMapGranularity)
